import clsx from "clsx";
import { AnimatePresence, type HTMLMotionProps, motion } from "motion/react";
import {
  createContext,
  use,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Btn } from "../btn/Btn";
import { Radio, type RadioProps } from "../radio/Radio";

type DialogPropsToOmit = "onClose";

export interface DialogProps extends Omit<
  HTMLMotionProps<"dialog">,
  DialogPropsToOmit
> {
  ref?: React.RefObject<DialogRef | null>;
  onClose?: (fn?: () => void) => void;
  size?: "lg" | "sm";
  className?: string;
  duration?: number;
  dismiss?: boolean;
}

export interface DialogRef extends HTMLDialogElement {
  context: DialogContextProps;
}

export interface DialogContextProps {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  open: boolean;
  close: (closeAction?: () => void) => void;
}

const DialogContext = createContext<DialogContextProps>({
  dialogRef: {
    current: null,
  },
  open: false,
  close: () => {},
});

export const Dialog: React.FC<React.PropsWithChildren<DialogProps>> & {
  Context: typeof DialogContext;
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
  Title: typeof Title;
  Text: typeof Text;
  Close: typeof Close;
  Group: typeof DialogGroup;
  Grid: typeof DialogGrid;
  Radio: typeof DialogRadio;
} = ({
  onClose,
  className,
  size = "sm",
  children,
  duration = 0.5,
  dismiss = true,
  initial = { opacity: 0, translateY: "1rem" },
  animate = { opacity: 1, translateY: 0 },
  exit = { opacity: 0, translateY: "-1rem" },
  transition = { duration, ease: [0.175, 0.885, 0.32, 1.275] },
  variants,
  ref,
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dialogCloseActionRef = useRef<() => void>(undefined);

  const [open, setOpen] = useState(true);

  const context: DialogContextProps = {
    dialogRef,
    open,
    close: (closeAction) => {
      dialogCloseActionRef.current = closeAction;
      setOpen(false);
    },
  };

  useImperativeHandle(ref, () => {
    const exposedRef = (dialogRef.current ?? {}) as DialogRef;

    exposedRef.context = context;

    return exposedRef;
  });

  const onDismiss: React.PointerEventHandler = (event) => {
    if (dismiss === true) {
      const isOutside =
        event.target instanceof Node &&
        dialogRef.current?.contains(event.target) === false;

      if (isOutside === true) {
        setOpen(false);
      } else if (
        dialogRef.current !== null &&
        event.target === dialogRef.current
      ) {
        const rect = dialogRef.current.getBoundingClientRect();

        const isOutsideOfDialogRect =
          event.clientY < rect.top ||
          event.clientY > rect.bottom ||
          event.clientX < rect.left ||
          event.clientX > rect.right;

        if (isOutsideOfDialogRect === true) {
          setOpen(false);
        }
      }
    }
  };

  const onDialogClose: React.ReactEventHandler = (event) => {
    event.preventDefault();

    if (dismiss === true) {
      setOpen(false);
    }
  };

  const onDialogKeyDown: React.KeyboardEventHandler = (event) => {
    if (dismiss === false && event.code === "Escape") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (open === true && dialogRef.current?.open === false) {
      dialogRef.current?.showModal();
    }
  });

  return createPortal(
    <DialogContext.Provider value={context}>
      <AnimatePresence
        onExitComplete={async () => {
          onClose?.(dialogCloseActionRef.current);
        }}
      >
        {open === true && (
          <motion.dialog
            ref={dialogRef}
            className={clsx("dialog", "motion", `dialog-${size}`, className)}
            initial={initial}
            animate={animate}
            exit={exit}
            transition={transition}
            variants={variants}
            onPointerDown={onDismiss}
            onCancel={onDialogClose}
            onKeyDown={onDialogKeyDown}
            role="dialog"
            aria-modal="true"
            {...props}
          >
            {children}
          </motion.dialog>
        )}
      </AnimatePresence>
    </DialogContext.Provider>,
    document.body,
  );
};

const Header: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-header", className)}>{children}</div>;
};

const Body: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-body", className)}>{children}</div>;
};

const Footer: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-footer", className)}>{children}</div>;
};

const Title: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <h2 className={clsx("dialog-title", className)}>{children}</h2>;
};

const Text: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-text", className)}>{children}</div>;
};

const Close: React.FC<{
  className?: string;
  onClick?: () => void;
}> = ({ className, onClick }) => {
  const context = use(DialogContext);

  return (
    <Btn
      className={clsx("dialog-close", className)}
      icon="cross_24px_semibold"
      mode="close"
      size="sm"
      onClick={() => (onClick ?? context.close)?.()}
    />
  );
};

const DialogGroup: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-group", className)}>{children}</div>;
};

const DialogGrid: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  return <div className={clsx("dialog-grid", className)}>{children}</div>;
};

const DialogRadio: React.FC<
  RadioProps & {
    text?: string;
    className?: string;
  }
> = ({ text, className, ...props }) => {
  const id = useId();

  return (
    <div className={clsx("dialog-radio", className)}>
      <Radio {...props} id={props.id ?? id} />
      {text !== undefined && (
        <label className="dialog-radio-text" htmlFor={props.id ?? id}>
          {text}
        </label>
      )}
    </div>
  );
};

export const useDialogContext = () => {
  return use(DialogContext);
};

Dialog.Context = DialogContext;
Dialog.Header = Header;
Dialog.Body = Body;
Dialog.Footer = Footer;
Dialog.Title = Title;
Dialog.Text = Text;
Dialog.Close = Close;
Dialog.Group = DialogGroup;
Dialog.Grid = DialogGrid;
Dialog.Radio = DialogRadio;
